import React, { useCallback, useEffect, useState } from "react"
import CommonSelect from "../../../core/common/commonSelect"
import { Option } from "../../../core/common/commonSelect"
import { getAllStudents } from "../../../service/student"
import { DEFAULT_BRANCH_ID } from "../../../constant"

export interface TransportStudentDropdownProps {
  selectedSystem: { _id?: string; value?: string } | null
  value: string
  onChange: (studentId: string) => void
  onSelectStudent?: (student: any) => void
  placeholder?: string
  className?: string
  /** When editing, pass the current student option so it shows even if not in fetched list */
  optionForCurrentValue?: { value: string; label: string } | null
  disabled?: boolean
}

const studentToOption = (s: any): Option => {
  const first =
    s.user?.firstName ??
    s.admission?.firstName ??
    s.firstName ??
    s.first_name ??
    ""
  const last =
    s.user?.lastName ??
    s.admission?.lastName ??
    s.lastName ??
    s.last_name ??
    ""
  const name = `${first} ${last}`.trim()
  const gr = s.grNumber ?? s.gr_number ?? "N/A"
  const label = name ? `${name} - GR: ${gr}` : `GR: ${gr}`
  return { value: s._id, label }
}

const TransportStudentDropdown: React.FC<TransportStudentDropdownProps> = ({
  selectedSystem,
  value,
  onChange,
  onSelectStudent,
  placeholder = "Select Student",
  className = "select",
  optionForCurrentValue,
  disabled,
}) => {
  const [options, setOptions] = useState<Option[]>([])
  const [studentMap, setStudentMap] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(false)

  const loadStudents = useCallback(() => {
    const systemId = selectedSystem?._id || selectedSystem?.value
    if (!systemId) {
      setOptions([])
      setStudentMap({})
      return
    }
    setLoading(true)
    getAllStudents({
      system: systemId,
      ...(DEFAULT_BRANCH_ID && { branch: DEFAULT_BRANCH_ID }),
      limit: 500,
      _t: Date.now(),
    } as any)
      .then(res => {
        const raw = res as any
        const list = Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.students)
            ? raw.students
            : Array.isArray(raw?.data?.data)
              ? raw.data.data
              : Array.isArray(raw?.data?.students)
                ? raw.data.students
                : []
        setOptions(list.map((s: any) => studentToOption(s)))
        const map: Record<string, any> = {}
        list.forEach((s: any) => { map[s._id] = s })
        setStudentMap(map)
      })
      .catch(() => { setOptions([]); setStudentMap({}) })
      .finally(() => setLoading(false))
  }, [selectedSystem?._id, selectedSystem?.value])

  useEffect(() => {
    loadStudents()
  }, [loadStudents])

  const displayOptions: Option[] =
    optionForCurrentValue &&
    value === optionForCurrentValue.value &&
    !options.some(o => o.value === value)
      ? [optionForCurrentValue, ...options]
      : options

  const selectedOption =
    displayOptions.find(o => o.value === value) || null

  return (
    <CommonSelect
      className={className}
      options={displayOptions}
      value={selectedOption}
      onChange={opt => {
        const id = opt?.value ?? ""
        onChange(id)
        if (onSelectStudent && id && studentMap[id]) {
          onSelectStudent(studentMap[id])
        } else if (onSelectStudent && !id) {
          onSelectStudent(null)
        }
      }}
      placeholder={loading ? "Loading students..." : placeholder}
      isDisabled={disabled}
    />
  )
}

export default TransportStudentDropdown
